import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ActivityEntryDto } from '@/api/generated';
import { useActivity, useExportActivityCsv } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { dutchVerb, formatShortDate, iconForVerb, parsePayload, toNumber } from '@/lib/format';
import styles from './Activity.module.css';

// Verb prefix → filter checkbox label. F13 ships client-side filtering; BF-4
// captures the backend follow-up for server-side filtering once the activity
// log grows past 1k events per project.
const TYPES = [
  { label: 'mail', defaultActive: true },
  { label: 'photo', defaultActive: true },
  { label: 'invoice', defaultActive: true },
  { label: 'document', defaultActive: true },
  { label: 'task', defaultActive: true },
  { label: 'decision', defaultActive: true },
] as const;

type FilterKey = (typeof TYPES)[number]['label'];

export function Activity() {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<ActivityEntryDto[]>([]);
  const [seenPages, setSeenPages] = useState<Set<number>>(() => new Set());
  const [activeFilters, setActiveFilters] = useState<Record<FilterKey, boolean>>(() => {
    const init = {} as Record<FilterKey, boolean>;
    for (const t of TYPES) init[t.label] = t.defaultActive;
    return init;
  });

  const activity = useActivity(page);
  const exportCsv = useExportActivityCsv();

  // Accumulate items across pages — clicking "Toon oudere events" pages up
  // through the list. We track seen pages so a TanStack Query refetch
  // doesn't double-add items into the accumulated list.
  useEffect(() => {
    if (!activity.data) return;
    if (seenPages.has(page)) return;
    setSeenPages((prev) => {
      const next = new Set(prev);
      next.add(page);
      return next;
    });
    const incoming = activity.data.items;
    setAccumulated((prev) => [...prev, ...incoming]);
  }, [activity.data, page, seenPages]);

  const items = accumulated;
  const counts = useMemo(() => countByVerbPrefix(items), [items]);
  const visibleItems = useMemo(
    () => items.filter((e) => activeFilters[verbPrefix(e.verb) as FilterKey] !== false),
    [items, activeFilters],
  );

  // Server tells us the total; "Toon oudere events" enables until we've
  // accumulated everything. activity.data.total is the source of truth on
  // the latest page (it doesn't change between pages).
  const total = toNumber(activity.data?.total ?? 0);
  const hasMore = items.length < total;

  const toggleFilter = (key: FilterKey) => {
    setActiveFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <AppFrame>
      <QueryBoundary query={activity} loading="Activiteit laden…">
        {(pageData) => (
          <>
            <div className={`row ${styles.headerRow}`}>
              <div>
                <div className="eyebrow">
                  Activiteitenlog · onveranderlijk · {toNumber(pageData.total)} events
                </div>
                <h1 className={styles.title}>Activiteitenlog</h1>
                <p className={styles.subtitle}>
                  Wie wijzigde wanneer wat. Bewijsmateriaal bij latere discussies — niet bewerkbaar.
                </p>
              </div>
              <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                <Btn
                  onClick={() => exportCsv.mutate()}
                  disabled={exportCsv.isPending}
                  aria-label="Export CSV"
                >
                  <Icon name="download" size={14} /> {exportCsv.isPending ? 'Bezig…' : 'Export CSV'}
                </Btn>
              </div>
            </div>

            <div className={styles.body}>
              <Card className={styles.list}>
                {visibleItems.length === 0 && (
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Geen events geregistreerd.
                  </div>
                )}
                {visibleItems.map((e, i) => {
                  const day = formatShortDate(e.at);
                  const prevDay = i > 0 ? formatShortDate(visibleItems[i - 1].at) : null;
                  return (
                    <Fragment key={e.id}>
                      {day !== prevDay && (
                        <div className={`row gap-8 ${styles.dayHeader}`}>
                          <span className="eyebrow">{day}</span>
                          <div className={styles.dayRule} />
                        </div>
                      )}
                      <Entry entry={e} />
                    </Fragment>
                  );
                })}
                {hasMore && (
                  <div className={`row ${styles.olderRow}`}>
                    <Btn
                      variant="ghost"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={activity.isFetching}
                    >
                      {activity.isFetching ? 'Bezig…' : 'Toon oudere events'}
                    </Btn>
                  </div>
                )}
              </Card>

              <div className="col gap-12">
                <Card className={styles.filterCard}>
                  <h3 className={styles.filterTitle}>Filter op type</h3>
                  {TYPES.map((t) => (
                    <label key={t.label} className={`row gap-6 ${styles.filterRow}`}>
                      <input
                        type="checkbox"
                        checked={activeFilters[t.label]}
                        onChange={() => toggleFilter(t.label)}
                        className={styles.checkbox}
                      />
                      <span className="flex-1">{t.label}</span>
                      <span className={`mono ${styles.filterCount}`}>
                        {counts.get(t.label) ?? 0}
                      </span>
                    </label>
                  ))}
                </Card>

                <Card className={styles.retentionCard}>
                  <h3 className={styles.retentionTitle}>Bewaring</h3>
                  <p className={styles.retentionBody}>
                    Activiteit wordt 11 jaar bijgehouden — 10-jarige aansprakelijkheid + 1 jaar
                    marge.
                  </p>
                </Card>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>
    </AppFrame>
  );
}

function Entry({ entry }: { entry: ActivityEntryDto }) {
  const payload = parsePayload<Record<string, unknown>>(entry.payloadJson);
  const subj = subjectFor(entry, payload);
  const det = detailFor(entry, payload);
  const time = new Date(entry.at).toLocaleTimeString('nl-BE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={`row gap-12 ${styles.entry}`}>
      <span className={`mono ${styles.entryTime}`}>{time}</span>
      <div className={styles.entryIcon}>
        <Icon name={iconForVerb(entry.verb)} size={14} />
      </div>
      <div className="flex-1">
        <div className={styles.entryLine}>
          <strong className={styles.entryWho}>{entry.actorDisplayName}</strong>
          <span className={styles.entryAct}> {dutchVerb(entry.verb)} </span>
          <strong className={styles.entrySubj}>{subj}</strong>
        </div>
        {det && <div className={styles.entryDet}>{det}</div>}
      </div>
    </div>
  );
}

function subjectFor(entry: ActivityEntryDto, payload: Record<string, unknown> | undefined): string {
  if (!payload) return entry.targetType;
  if (entry.verb === 'mail.sent') return String(payload.subject ?? entry.targetType);
  if (entry.verb === 'document.uploaded') return String(payload.name ?? entry.targetType);
  if (entry.verb === 'task.done') return String(payload.title ?? entry.targetType);
  if (entry.verb === 'invoice.received') return String(payload.supplier ?? entry.targetType);
  if (entry.verb === 'photo.added') return `${String(payload.count ?? '')} foto's`;
  return entry.targetType;
}

function detailFor(entry: ActivityEntryDto, payload: Record<string, unknown> | undefined): string {
  if (!payload) return '';
  if (entry.verb === 'invoice.received' && typeof payload.amount === 'number') {
    return `€${payload.amount}`;
  }
  if (entry.verb === 'photo.added' && payload.room) return String(payload.room);
  if (entry.verb === 'document.uploaded' && payload.version) {
    return `versie ${payload.version}`;
  }
  return '';
}

function verbPrefix(verb: string): string {
  return verb.split('.')[0];
}

function countByVerbPrefix(items: ActivityEntryDto[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of items) {
    const prefix = verbPrefix(e.verb);
    out.set(prefix, (out.get(prefix) ?? 0) + 1);
  }
  return out;
}
